<template>
    <v-container fluid>
        <v-row>
            <v-col cols="12">
                <v-row class="mt-2 mb-3"
                       v-bind:class="isMobile ? ['mx-3'] : ['mx-5']"
                       justify="space-between">
                    <div role="heading" aria-level="1">
                        <span class="card-title">{{$t('dashboard-view.welcome-user')}}&nbsp;</span>
                        <span class="card-title font-weight-bold">{{this.user.firstname}}</span>
                    </div>
                    <v-hover v-if="hasOnboarding" v-slot="{hover}">
                        <v-btn :color="hover ? 'buttonHover' : 'primary'"
                               dark
                               depressed
                               class="text-none mr-9"
                               @click="onboardingClick"
                               :aria-label="$t('dashboard-view.addSubscription')">
                            {{$t('dashboard-view.addSubscription')}}
                        </v-btn>
                    </v-hover>
                </v-row>
                <v-card flat
                        :class="isMobile ? ['rounded-lg', 'mx-1'] : ['rounded-xl', 'mx-4']"
                        :loading="!loaded"
                        :aria-busy="!loaded ? 'true' : 'false'"
                        role="region"
                        :aria-label="$t('dashboard-view.reportsSection')">
                    <v-card-title 
                        class="card-title"
                        v-bind:class="isMobile ? ['ml-1', 'pl-2'] : ['ml-4']"
                        :id="reportsTitleId"
                        role="heading"
                        aria-level="2">
                        {{titlePart1}}&nbsp;<span class="card-title font-weight-medium mr-4">{{titlePart2.toLowerCase()}}</span>
                    </v-card-title>
                    <v-card-text 
                        class="d-flex flex-row flex-wrap"
                        v-bind:class="isMobile ? ['px-1', 'justify-center'] : ['']"
                        role="list"
                        :aria-labelledby="reportsTitleId">
                        <!-- Loading skeletons -->
                        <div v-if="!loaded" role="status" :aria-label="$t('accessibility.loading')">
                            <v-skeleton-loader class="rounded-lg px-4 ma-4" type="list-item-three-line" width="405px" height="124px" elevation="2" aria-hidden="true">
                            </v-skeleton-loader>
                            <v-skeleton-loader class="rounded-lg px-4 ma-4" type="list-item-three-line" width="405px" height="124px" elevation="2" aria-hidden="true">
                            </v-skeleton-loader>
                            <span class="sr-only">{{$t('accessibility.loading')}}</span>
                        </div>

                        <!-- Report Cards -->
                        <v-badge v-for="report in filteredReports" 
                                 :key="report.Id"
                                 bordered
                                 light
                                 left
                                 :color="$vuetify.theme.themes.light.linkHover"
                                 :content="report.DocumentCount - report.DocumentViewedCount"
                                 overlap
                                 :offset-x="isMobile ? '16px' : '32px'"
                                 :offset-y="isMobile ? '16px' : '28px'"
                                 :value="user.role == 'USER' && report.DocumentViewedCount >= 0 && (report.DocumentCount !== report.DocumentViewedCount)"
                                 :aria-label="getUnreadBadgeLabel(report)"
                                 role="listitem">
                            <v-card dark flat
                                    class="rounded-lg overflow-hidden report-card"
                                    v-bind:class="isMobile ? ['ma-1'] : ['ma-4']"
                                    :width="isMobile ? '305px' : '405px'"
                                    :color="tileColor ? tileColor : ''"
                                    min-height="135px"
                                    @click="selectReport(report.Code)"
                                    @keydown.enter="selectReport(report.Code)"
                                    @keydown.space.prevent="selectReport(report.Code)"
                                    tabindex="0"
                                    role="button"
                                    :aria-label="getReportCardLabel(report)"
                                    :aria-describedby="getReportDescriptionId(report.Code)">

                                <loginSVG v-if="!tileColor" 
                                          class="loginSVG"
                                          :colors="loginSVGColors"
                                          role="presentation"
                                          aria-hidden="true">
                                </loginSVG>

                                <v-row class="px-4">
                                    <v-card-title 
                                        class="text-large better-break c-zindex"
                                        v-bind:class="SVGFontColor ? 'SVGFontColor' : ''"
                                        role="heading"
                                        aria-level="3">
                                        {{$i18n.locale == 'fr' ? report.TitleFr : report.TitleEn}}
                                    </v-card-title>
                                    <v-card-text 
                                        class="c-zindex"
                                        :id="getReportDescriptionId(report.Code)">
                                        <span :class="SVGFontColor ? 'SVGFontColor' : 'white--text'">{{report.DocumentCount}} &nbsp;</span>
                                        <span :class="SVGFontColor ? 'SVGFontColor' : 'white--text'">{{$t('dashboard-view.documents')}}</span>
                                    </v-card-text>
                                    <v-img v-if="report.ThumbnailInfo"
                                           :src="`../../assets/Images/${report.ThumbnailInfoJSON.filename}`"
                                           class="thumbnailLogo"
                                           position="right"
                                           :width="report.ThumbnailInfoJSON.width"
                                           :height="report.ThumbnailInfoJSON.height"
                                           :alt="$t('dashboard-view.reportThumbnail', { title: ($i18n.locale == 'fr' ? report.TitleFr : report.TitleEn) })"
                                           contain>
                                    </v-img>
                                </v-row>
                            </v-card>
                        </v-badge>

                        <!-- No reports message -->
                        <div v-if="loaded && filteredReports.length === 0" 
                             class="pa-4"
                             role="status">
                            <p>{{$t('dashboard-view.noReports')}}</p>
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
        <onboarding v-model="showOnBoardingModal" @close="showOnBoardingModal = false" @subscribe="onSubscribe"></onboarding>
        <!--Contient les rapport visualisés-->
        <v-row v-if="selectedReportCodes.length">
            <v-col cols="12" class="pa-0">
                <documentList v-for="reportProps in selectedReportCodes"
                            v-bind:report-code="reportProps.code"
                            :key="reportProps.code"
                            :displayProperties="reportProps.properties"
                            :filterUserId="filterUserId"
                            @unselectReport="unselectReport">
                </documentList>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
    import { mapState } from "vuex";
    import documentList from "../../components/documentList"
    import loginSVG from '../../components/loginSVG.vue'
    import onboarding from '../../components/onboarding.vue'
    import { generateId } from '../../utils/accessibility';

    export default {
        name: "dashboard",
        components: { loginSVG, documentList, onboarding },
        data() {
            return {
                reportsInfos: [],
                selectedReportCodes: new Array(0),
                viewModel: {},
                loaded: false,
                showOnBoardingModal: false,
                search: "",
                sortedAttribute: "",
                sortedWay: "asc",
                hasOnboarding: false,
                editedUser: {},
                filterUserId: null,
                reportsTitleId: generateId('reports-title')
            }
        },
        created: function () {
            document.body.classList.remove('loading');
        },
        mounted: function () {
            this.getReports();
        },
        methods: {
            getReportDescriptionId(reportCode) {
                return `report-desc-${reportCode}`;
            },

            getReportCardLabel(report) {
                const title = this.$i18n.locale == 'fr' ? report.TitleFr : report.TitleEn;
                const unreadCount = report.DocumentCount - report.DocumentViewedCount;
                const hasUnread = this.user.role == 'USER' && report.DocumentViewedCount >= 0 && unreadCount > 0;

                let label = `${title}. ${report.DocumentCount} ${this.$t('dashboard-view.documents')}`;

                if (hasUnread) {
                    label += `. ${unreadCount} ${this.$t('dashboard-view.unreadDocuments')}`;
                }

                return label;
            },

            getUnreadBadgeLabel(report) {
                const unreadCount = report.DocumentCount - report.DocumentViewedCount;
                if (this.user.role == 'USER' && report.DocumentViewedCount >= 0 && unreadCount > 0) {
                    return `${unreadCount} ${this.$t('dashboard-view.unreadDocuments')}`;
                }
                return '';
            },

            getReports() {
                var self = this;
                self.loaded = false;
                self.reportsInfos = [];
                self.selectedReportCodes = new Array(0);
                self.viewModel = {};

                // Announce loading to screen readers
                self.$announcer.polite(self.$t('accessibility.loading'));

                self.$axios.get("Dashboard/GetReports")
                    .then(response => {
                        if (response.data.success == false && response.data.error == 'session-expired') {
                            self.$router.push({ name: 'login' });
                        } 
                        self.initDashboard(response.data);
                    })
                    .catch(function (error) {
                        console.log(error);
                        // Announce error to screen readers
                        self.$announcer.assertive(self.$t('accessibility.error'));
                    })

            },
            // Initialise le tableau de bord
            initDashboard(viewModel) {
                this.viewModel = viewModel;
                this.loaded = true;
                this.reportsInfos = viewModel.ReportsInfos;
                this.hasOnboarding = viewModel.HasOnboarding;

                for (let i = 0; i < this.reportsInfos.length; i++) { 
                    if (this.reportsInfos[i].ThumbnailInfo)
                        this.reportsInfos[i].ThumbnailInfoJSON = JSON.parse(this.reportsInfos[i].ThumbnailInfo);
                };

                if (this.$route.query.userId) {
                    this.filterUserId = parseInt(this.$route.query.userId);
                    this.selectedReportCodes = this.reportsInfos.map(r => ({ code: r.Code, properties: r.DisplayProperties }));
                }

                // Announce loaded content to screen readers
                const reportsCount = this.reportsInfos.length;
                this.$announcer.polite(
                    this.$t('dashboard-view.reportsLoaded', { count: reportsCount })
                );
            },

            selectReport(reportCode) {
                var self = this;

                var reportDisplayedAt = self
                    .selectedReportCodes
                    .findIndex(x => x.code == reportCode);

                if (reportDisplayedAt >= 0) {
                    self.selectedReportCodes.splice(reportDisplayedAt, 1)
                }
                var reportIndex = self.reportsInfos.findIndex(x => x.Code == reportCode);
                const report = self.reportsInfos[reportIndex];
                const reportTitle = self.$i18n.locale == 'fr' ? report.TitleFr : report.TitleEn;

                self.selectedReportCodes = [{ code: reportCode, properties: report.DisplayProperties }].concat(self.selectedReportCodes);

                // Announce report selection to screen readers
                self.$announcer.polite(
                    self.$t('dashboard-view.reportSelected', { title: reportTitle })
                );
            },
            unselectReport(reportCode) {
                var self = this,
                    selectedReportIndex = self.selectedReportCodes.findIndex(x => x.code == reportCode),
                    isSelected = selectedReportIndex > -1;

                if (isSelected) {
                    self.$delete(self.selectedReportCodes, selectedReportIndex);
                }

            },
            sort(byAttribute) {
                var self = this;

                // Remove state class(desc/asc) from previous sorted columns 
                if (self.sortedAttribute != null && self.sortedAttribute != "") {
                    document.getElementById(self.sortedAttribute).classList.remove("asc");
                    document.getElementById(self.sortedAttribute).classList.remove("desc");
                }


                if (self.sortedAttribute == byAttribute && self.sortedWay == "asc") {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return 1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return -1;
                        } else {
                            return 0;
                        }
                    });
                    document.getElementById(self.sortedAttribute).classList.add("desc");
                    self.sortedWay = "desc";
                } else if (self.sortedAttribute == byAttribute && self.sortedWay == "desc") {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return -1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return 1;
                        } else {
                            return 0;
                        }

                    });
                    document.getElementById(self.sortedAttribute).classList.add("asc");
                    self.sortedWay = "asc";
                } else {
                    self.reportsInfos.sort(function (a, b) {
                        if (a[byAttribute] < b[byAttribute]) {
                            return -1;
                        } else if (a[byAttribute] > b[byAttribute]) {
                            return 1;
                        } else {
                            return 0;
                        }

                    });


                    document.getElementById(byAttribute).classList.add("asc");
                    self.sortedAttribute = byAttribute;
                    self.sortedWay = "asc";
                }

            },
            onboardingClick: function () {
                this.showOnBoardingModal = true;
            },
            onSubscribe() {
                this.showOnBoardingModal = false;
                this.getReports();
            },
        },
        computed: {
            ...mapState("account", ["user"]),

            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            },
            filteredReports: function () {
                var self = this; 

                if (self.reportsInfos === undefined) return [];

                return this.reportsInfos.filter(function (report) {
                    var reportTitle = report.TitleEn.toLowerCase(),
                        searchTerm = self.search.toLowerCase();
                    if (reportTitle == null) { return false; }
                    return reportTitle.includes(searchTerm);
                });
            },
            fullname: function () {
                return this.user.lastname + ', ' + this.user.firstname;
            },
            loginSVGColors() {
                return this.$vuetify.theme.themes.light.loginSVG;
            },
            experienceCode: function () {
                return this.viewModel?.Experience;
            },
            titlePart1() {
                return this.$i18n.locale == 'fr' ? this.$t('dashboard-view.documentTypes') : this.$t('dashboard-view.available');
            },
            titlePart2() {
                return this.$i18n.locale == 'fr' ? this.$t('dashboard-view.available') : this.$t('dashboard-view.documentTypes');;
            },
            tileColor() {
                return this.$experience.theme.themes.light.tileColor;
            },
            SVGFontColor() {
                return this.$experience.theme.themes.light.SVGFontColor;
            }
        }
        
    }
</script>

<style scoped>
    .blur {
        filter:blur(6px) !important;
    }
    .c-zindex {
        z-index: 1 !important;
    }
    .loginSVG {
        position: absolute !important;
        height: 100% !important;
        width: 100% !important;
        z-index: 0 !important;
    }
    .v-application .SVGFontColor {
        color: var(--SVGFontColor);
        background-color: transparent !important;
    }
    .thumbnailLogo {
        position:absolute;
        bottom:0px;
        right:5px;
    }

    /* Accessibility: Focus states for report cards */
    .report-card {
        cursor: pointer;
        transition: transform 0.2s ease, box-shadow 0.2s ease;
    }

    .report-card:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .report-card:focus {
        outline: 3px solid #1976D2;
        outline-offset: 2px;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }

    .report-card:active {
        transform: translateY(0);
    }

    /* High contrast mode support */
    @media (prefers-contrast: high) {
        .report-card:focus {
            outline: 4px solid currentColor;
            outline-offset: 3px;
        }
    }


</style>